import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Bot, ChevronRight, BookOpen, ExternalLink, LifeBuoy, ArrowLeft, Plus, Paperclip, FileText } from 'lucide-react';
import * as api from '../services/api';
import { BRAND_NAME } from '../utils/brand';

// ─── 問い合わせ（サポートチャット）────────────────────────────────────
interface SupportThread {
  id: string; subject: string; status: string;
  unread_count: number; last_message?: string; updated_at: string;
}
interface SupportMessage {
  id: string; sender_type: 'admin' | 'super' | 'system'; sender_name: string;
  body: string; created_at: string;
  attachment_path?: string | null; attachment_name?: string | null; attachment_mime?: string | null;
}

function authHeaders(): Record<string, string> {
  const t = api.getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// 認証付きで添付ファイルを取得して表示する（画像はプレビュー、PDF等はダウンロードチップ）
function AttachmentView({ messageId, name, mime }: { messageId: string; name: string; mime: string }) {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const isImage = mime.startsWith('image/');
  const fileUrl = `${api.getApiBase()}/support/attachments/${messageId}`;

  useEffect(() => {
    if (!isImage) return;
    let url: string | null = null; let cancelled = false;
    fetch(fileUrl, { headers: authHeaders(), credentials: 'include' })
      .then(r => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(b => { if (cancelled) return; url = URL.createObjectURL(b); setObjUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [fileUrl, isImage]);

  const download = async () => {
    try {
      const r = await fetch(fileUrl, { headers: authHeaders(), credentials: 'include' });
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u; a.download = name; a.click();
      URL.revokeObjectURL(u);
    } catch { /* ignore */ }
  };

  if (isImage) {
    return objUrl
      ? <img src={objUrl} alt={name} onClick={download} title="クリックで保存"
          style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, cursor: 'pointer', display: 'block', marginTop: 4 }} />
      : <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>画像を読み込み中...</div>;
  }
  return (
    <button onClick={download}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, padding: '6px 10px', background: 'rgba(255,255,255,0.85)', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#0F172A', fontWeight: 600, maxWidth: '100%' }}>
      <FileText size={13} color="#DC2626" style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </button>
  );
}

// 回答に付けるリンク。to が /manual で始まるものはマニュアル章、それ以外は画面への遷移
interface FaqLink {
  label: string;
  to: string;
}

interface Message {
  role: 'user' | 'bot';
  content: string;
  suggestions?: string[];
  links?: FaqLink[];
}

// ─── FAQ データベース ──────────────────────────────────────────────────────────
// 操作マニュアル(/manual = docs/manuals/admin/*.md)と対応付けて管理する。
// 機能を追加・変更したら、マニュアルとこのFAQの両方を更新すること。

interface FaqEntry {
  keywords: string[];
  answer: string;
  followup?: string[];
  links?: FaqLink[];
}

const FAQ: FaqEntry[] = [
  // ── 打刻・勤怠 ──────────────────────────────────────────────
  {
    keywords: ['打刻', 'できない', '業務開始', '業務終了', 'ボタン'],
    answer: '打刻できない主な原因は「船舶への配置がされていない」ことです。管理者に「乗組員配置」画面で該当船舶への配置を依頼してください。\n配置済みの場合は、ブラウザをリロードするか再ログインをお試しください。',
    followup: ['乗組員配置の方法を知りたい', 'ログインできない'],
    links: [
      { label: '乗組員配置を開く', to: '/crew-assign' },
      { label: 'マニュアル: 00 はじめに', to: '/manual?ch=00' },
    ],
  },
  {
    keywords: ['勤怠', '一覧', '確認', '打刻状況'],
    answer: '「勤怠一覧」メニューで全船員の打刻状況をリアルタイムで確認できます。\n日付・船員・船舶でフィルタリングでき、業務開始/終了時刻や業務時間を一覧で確認できます。',
    followup: ['レポートを出力したい', '打刻の終了忘れがあった'],
    links: [
      { label: '勤怠一覧を開く', to: '/attendance' },
      { label: 'マニュアル: 04 勤怠とクルーイング', to: '/manual?ch=04' },
    ],
  },
  {
    keywords: ['消し忘れ', '終了忘れ', '退勤忘れ', '業務未終了', '押し忘れ'],
    answer: '業務終了の打刻を忘れた場合：\n• 24時間以上続いた勤務は「業務未終了」アラートが発生します\n• 開始から24時間を超えた打刻は、船舶の運航状態の判定にも使われなくなります\n• 管理者は「勤怠一覧」から打刻を修正できます',
    followup: ['アラートが出た場合は？'],
    links: [
      { label: '勤怠一覧を開く', to: '/attendance' },
      { label: 'アラートを開く', to: '/alerts' },
    ],
  },
  {
    keywords: ['休息', '規制', '休息時間', '拘束'],
    answer: '「休息時間」メニューで、法定休息時間の遵守状況を船員ごとに確認できます。\n休息が不足している船員は警告表示されます。',
    followup: ['アラートが出た場合は？'],
    links: [
      { label: '休息時間を開く', to: '/rest-hours' },
      { label: 'マニュアル: 04 勤怠とクルーイング', to: '/manual?ch=04' },
    ],
  },
  // ── 運航状態・GPS ────────────────────────────────────────────
  {
    keywords: ['状態', '色', '航行中', '停泊中', '入港中', 'オフライン', '色分け', '自動判定'],
    answer: '船舶の運航状態は、クルーの打刻（作業種別）から自動で判定されます。管理者による設定は不要です。\n\n🔵 青 = 航行中（「航海」「当直」で勤務中のクルーがいる）\n🟢 緑 = 入港中（「荷役」で勤務中のクルーがいる）\n⚪ グレー = 停泊中（上記に該当しない）\n⚫ 濃グレー = オフライン（GPSが8時間以上途絶）\n\nGPS画面の右下に凡例が常時表示されています。',
    followup: ['GPSの見方を知りたい'],
    links: [
      { label: 'GPS位置情報を開く', to: '/gps' },
      { label: 'マニュアル: 02 運航管理', to: '/manual?ch=02' },
    ],
  },
  {
    keywords: ['GPS', '位置', '地図', '航跡'],
    answer: '「GPS位置情報」メニューで船舶の現在位置と航跡を地図で確認できます。\n• 期間を選んで航跡の再生（タイムライン）が可能\n• 船の色は運航状態（航行中/入港中/停泊中/オフライン）を表します\n• 海域区分や気象などのレイヤー表示も切り替えできます',
    followup: ['船の色（運航状態）の意味は？'],
    links: [
      { label: 'GPS位置情報を開く', to: '/gps' },
      { label: 'マニュアル: 02 運航管理', to: '/manual?ch=02' },
    ],
  },
  // ── クルーイング（配乗計画）──────────────────────────────────
  {
    keywords: ['配乗', 'クルーイング', 'シフト', '勤務予定', '計画'],
    answer: '配乗計画（クルーイング）機能は現在制作中です（Coming Soon）。\nクルー×日付で勤務予定を組み、休息規制の自動チェックや計画表の出力を予定しています。\n正式リリースまでお待ちください。',
    followup: ['休息時間について知りたい'],
    links: [
      { label: '配乗計画を開く', to: '/crew-deployment' },
      { label: 'マニュアル: 04 勤怠とクルーイング', to: '/manual?ch=04' },
    ],
  },
  // ── アラート ────────────────────────────────────────────────
  {
    keywords: ['アラート', '通知', '警告', '退勤', '未打刻'],
    answer: 'アラートには以下の種類があります：\n• 業務未終了（24時間以上業務中のまま）\n• 残業超過（月間60時間超）\n• 連続勤務（14日以上）\n• アルコール検知（設定した警告値以上）\n\nアラート一覧は「アラート」メニューから確認・解決できます。',
    followup: ['アラートの閾値を変更したい', 'アルコール検知について'],
    links: [
      { label: 'アラートを開く', to: '/alerts' },
      { label: 'マニュアル: 04 勤怠とクルーイング', to: '/manual?ch=04' },
    ],
  },
  {
    keywords: ['閾値', '設定', 'アラート設定', '基準値'],
    answer: 'アラートの閾値は「アラート」→「閾値設定」タブから変更できます。\n会社全体・船別・船員別の3レベルで設定でき、細かい単位で管理できます。\n個別設定がない場合は上位レベルの設定が適用されます。',
    followup: ['アラートの種類について'],
    links: [{ label: 'アラートを開く', to: '/alerts' }],
  },
  {
    keywords: ['アルコール', '飲酒', '検知', '0.015'],
    answer: 'アルコール検知アラートは業務開始時に「警告」の閾値以上が検知された場合に発生します。\n「重大」の閾値以上は法定基準値超過として重大アラートになります。\n検知された場合は上長への確認が必要です。\n\n閾値は「アラート」→「閾値設定」タブの「アルコール」で、会社全体・船別・船員別に設定できます（初期値は警告0.015mg/L・重大0.150mg/L）。',
    followup: ['アラートの閾値を変更したい'],
    links: [{ label: 'アラートを開く', to: '/alerts' }],
  },
  // ── レポート・集計 ──────────────────────────────────────────
  {
    keywords: ['レポート', '出力', 'CSV'],
    answer: 'レポートは「レポート」メニューから出力できます。\n• 個人別・期間指定での勤怠集計\n• CSV出力が可能',
    followup: ['月次集計を見たい', '勤怠一覧の見方'],
    links: [
      { label: 'レポートを開く', to: '/reports' },
      { label: 'マニュアル: 05 レポート', to: '/manual?ch=05' },
    ],
  },
  {
    keywords: ['月次', '集計'],
    answer: '「月次集計」メニューで全船員の月単位の勤務実績（労働時間・休息・残業など）をまとめて確認できます。\n対象月・船舶・船員で絞り込みができます。',
    followup: ['レポートを出力したい'],
    links: [
      { label: '月次集計を開く', to: '/monthly' },
      { label: 'マニュアル: 05 レポート', to: '/manual?ch=05' },
    ],
  },
  // ── ユーザー・船舶 ──────────────────────────────────────────
  {
    keywords: ['ログイン', 'パスワード', '入れない', 'できない', '認証'],
    answer: 'ログインには「社員コード」と「パスワード」を使用します。\nパスワードを忘れた場合は、管理者（システム管理者または陸上管理者）にパスワードのリセットを依頼してください。\n管理者はユーザー管理画面から変更できます。',
    followup: ['パスワードを変更したい', 'ユーザーを追加したい'],
    links: [{ label: 'マニュアル: 00 はじめに', to: '/manual?ch=00' }],
  },
  {
    keywords: ['パスワード', '変更', 'リセット'],
    answer: '自分のパスワードはサイドバー下部のアバターをクリックすると変更できます。\n他のユーザーのパスワードは「ユーザー管理」→ 対象ユーザーを選択 → 編集から変更できます。',
    followup: ['ユーザー管理の使い方'],
    links: [{ label: 'ユーザー管理を開く', to: '/users' }],
  },
  {
    keywords: ['ユーザー', '船員', '追加', '登録', '新規'],
    answer: '船員の追加は「ユーザー管理」→「新規追加」から行えます。\n必要情報：社員コード・氏名・ロール（船員/船長/陸上管理者）・パスワード\nCSVでの一括インポートも可能です。\n追加後は「乗組員配置」で船舶への配置も忘れずに行ってください。',
    followup: ['乗組員配置の方法を知りたい'],
    links: [
      { label: 'ユーザー管理を開く', to: '/users' },
      { label: 'マニュアル: 03 乗組員', to: '/manual?ch=03' },
    ],
  },
  {
    keywords: ['配置', '乗組員', 'アサイン', '割り当て'],
    answer: '乗組員配置はサイドバーの「乗組員配置」メニューから行えます。\n① 配置したい船員を選択 → ② 乗船させる船舶を選択 → ③「乗船」ボタンをクリック\n配置が完了すると船員アプリから打刻できるようになります。',
    followup: ['船員を追加したい', '打刻できない'],
    links: [
      { label: '乗組員配置を開く', to: '/crew-assign' },
      { label: 'マニュアル: 03 乗組員', to: '/manual?ch=03' },
    ],
  },
  {
    keywords: ['船舶', '追加', '登録', '船'],
    answer: '船舶の追加は「船舶管理」→「新規追加」から行えます。\n船名・船種・IMO番号などを入力して登録します。CSVでの一括インポートも可能です。\n登録後は「乗組員配置」で船員を配置してください。',
    followup: ['乗組員配置の方法を知りたい', '船舶の画像を変えたい'],
    links: [
      { label: '船舶管理を開く', to: '/vessels' },
      { label: 'マニュアル: 02 運航管理', to: '/manual?ch=02' },
    ],
  },
  {
    keywords: ['画像', 'アイコン', 'アバター', '写真'],
    answer: 'ユーザーの顔写真は「ユーザー管理」、船舶の画像は「船舶管理」で、それぞれアイコン部分をクリックするとアップロード・変更できます。\n登録マスタの船舶カードなど、他の画面にも同じ画像が表示されます。',
    followup: ['ユーザーを追加したい', '船舶を追加したい'],
    links: [
      { label: 'ユーザー管理を開く', to: '/users' },
      { label: '船舶管理を開く', to: '/vessels' },
    ],
  },
  // ── 労務・安全 ──────────────────────────────────────────────
  {
    keywords: ['有給', '休暇', '申請', '残日数'],
    answer: '「有給・休暇」メニューで、船員からの休暇申請の承認・却下や残日数の確認ができます。',
    followup: [],
    links: [
      { label: '有給・休暇を開く', to: '/leave' },
      { label: 'マニュアル: 06 労務と安全', to: '/manual?ch=06' },
    ],
  },
  {
    keywords: ['乗下船', '乗船', '下船', '記録'],
    answer: '「乗下船記録」メニューで、船員の乗船・下船の記録を登録・確認できます。\n履歴の参照も可能です。',
    followup: [],
    links: [
      { label: '乗下船記録を開く', to: '/boarding' },
      { label: 'マニュアル: 06 労務と安全', to: '/manual?ch=06' },
    ],
  },
  {
    keywords: ['ヒヤリハット', 'ヒヤリ', '事故', '安全', '報告'],
    answer: '「ヒヤリハット」メニューで、船員から報告されたヒヤリハット（危険事例）の確認と対応状況の管理ができます。',
    followup: [],
    links: [
      { label: 'ヒヤリハットを開く', to: '/incidents' },
      { label: 'マニュアル: 06 労務と安全', to: '/manual?ch=06' },
    ],
  },
  {
    keywords: ['証明書', '記録簿', '様式', '労務管理'],
    answer: '「証明書発行」メニューから各種証明書の発行や、労務管理記録簿（様式第十六号の五）のExcel出力ができます。\n複数乗船への対応や基準労働期間の設定も可能です。',
    followup: [],
    links: [
      { label: '証明書発行を開く', to: '/certificates' },
      { label: 'マニュアル: 07 システム設定', to: '/manual?ch=07' },
    ],
  },
  // ── 連絡・システム ──────────────────────────────────────────
  {
    keywords: ['連絡', 'お知らせ', 'メッセージ', '既読', '配信'],
    answer: '「連絡・お知らせ」メニューから船員への連絡やお知らせを配信できます。\n既読状況の確認も可能です。',
    followup: [],
    links: [
      { label: '連絡・お知らせを開く', to: '/messages' },
      { label: 'マニュアル: 04 勤怠とクルーイング', to: '/manual?ch=04' },
    ],
  },
  {
    keywords: ['マスタ', '業務種別', '作業種別', '種別', '項目'],
    answer: '「登録マスタ」で業務種別（航海・荷役・当直など）の追加・編集ができます。\n会社共通のマスタと、船舶ごとの業務種別設定の両方を管理できます。\n※「航海」「当直」「荷役」は船舶の運航状態の自動判定にも使われます。',
    followup: ['船の色（運航状態）の意味は？'],
    links: [
      { label: '登録マスタを開く', to: '/master/activities' },
      { label: 'マニュアル: 07 システム設定', to: '/manual?ch=07' },
    ],
  },
  {
    keywords: ['同期', 'ログ', 'オフライン同期'],
    answer: '「同期ログ」メニューで、船員アプリとサーバー間のデータ同期の履歴とエラーを確認できます。\n船上でオフラインだった打刻は、通信回復時に自動で同期されます。',
    followup: [],
    links: [
      { label: '同期ログを開く', to: '/sync-logs' },
      { label: 'マニュアル: 07 システム設定', to: '/manual?ch=07' },
    ],
  },
  {
    keywords: ['デバイス', 'タブレット', '端末'],
    answer: 'デバイス管理は「デバイス管理」メニューから行えます。\n船舶に設置されたタブレットなどのデバイス情報を管理できます。\nデバイスのGPSハートビートが船舶の位置情報として記録されます。',
    followup: ['GPSの見方を知りたい'],
    links: [
      { label: 'デバイス管理を開く', to: '/devices' },
      { label: 'マニュアル: 07 システム設定', to: '/manual?ch=07' },
    ],
  },
  // ── アプリ・アクセス ────────────────────────────────────────
  {
    keywords: ['アプリ', 'スマホ', 'Android', 'APK', '配布', 'QR'],
    answer: '船員向けAndroidアプリの配布は運営コンソールのQRコード機能を使います。\n運営管理者に依頼してQRコードを表示してもらい、スマホで読み取ってAPKをインストールしてください。\nPCからは {会社サブドメイン}.crew.unison-sea.com でもアクセスできます。',
    followup: ['PCからアプリにアクセスしたい'],
  },
  {
    keywords: ['PC', 'パソコン', 'ブラウザ', 'crew', 'クルー'],
    answer: 'PCから船員アプリにアクセスするには、ブラウザで\n{会社名}.crew.unison-sea.com\nを開いてください。（例：unison-tech.crew.unison-sea.com）\nスマホアプリと同じ機能が使えます。',
    followup: ['ログインできない'],
  },
  // ── 問い合わせ ──────────────────────────────────────────────
  {
    keywords: ['問い合わせ', '担当者', 'サポート', '連絡したい', '質問したい', '聞きたい', 'システム担当'],
    answer: 'FAQで解決しない場合は、システム担当者に直接お問い合わせいただけます。\n「担当者に問い合わせる」から件名と内容を送信すると、担当者に通知され、このチャット上でそのままやり取りできます。',
    followup: [],
    links: [{ label: '担当者に問い合わせる', to: 'support:new' }],
  },
  // ── マニュアル・用語 ────────────────────────────────────────
  {
    keywords: ['マニュアル', '使い方', 'ヘルプ', '操作方法', '説明書'],
    answer: '操作マニュアルはサイドバーの「操作マニュアル」からいつでも閲覧できます。\n画面ごとの操作手順や用語集がまとまっています。',
    followup: ['用語の意味を調べたい'],
    links: [{ label: '操作マニュアルを開く', to: '/manual' }],
  },
  {
    keywords: ['用語', '意味', 'とは', '用語集'],
    answer: 'システムで使われる用語（配乗計画=クルーイング、停泊中、休息規制など）の意味は、操作マニュアルの用語集にまとまっています。',
    followup: [],
    links: [{ label: '用語集を開く', to: '/manual?ch=99' }],
  },
];

const INITIAL_SUGGESTIONS = [
  '打刻できないのはなぜ？',
  '船の色（運航状態）の意味は？',
  'アラートが出た場合は？',
  '操作マニュアルを見たい',
];

// ─── FAQ検索ロジック ───────────────────────────────────────────────────────────

function findAnswer(query: string): { answer: string; followup?: string[]; links?: FaqLink[] } {
  const q = query.toLowerCase()
    .replace(/[、。！？!?]/g, ' ')
    .replace(/[\s]+/g, ' ');

  let best: FaqEntry | null = null;
  let bestScore = 0;

  for (const entry of FAQ) {
    const score = entry.keywords.filter(k => q.includes(k.toLowerCase())).length;
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  if (!best || bestScore === 0) {
    return {
      answer: 'ご質問の内容を正確に把握できませんでした。\n以下のキーワードで再度お試しください：「打刻」「アラート」「運航状態」「レポート」「ユーザー追加」など\n\n解決しない場合は「担当者に問い合わせる」から直接ご質問いただけます。担当者に通知され、このチャットでそのままやり取りできます。',
      followup: INITIAL_SUGGESTIONS,
      links: [
        { label: '担当者に問い合わせる', to: 'support:new' },
        { label: '操作マニュアルを開く', to: '/manual' },
      ],
    };
  }

  return { answer: best.answer, followup: best.followup, links: best.links };
}

// ─── コンポーネント ────────────────────────────────────────────────────────────

const ChatWidget: React.FC = () => {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);
  const navigate  = useNavigate();

  // ── フローティングボタンの位置（画面右下からのオフセット。ドラッグで移動・端末スナップ・localStorage保存） ──
  const DEFAULT_POS = { right: 20, bottom: 28 };
  const SNAP_MARGIN = 16;
  const [pos, setPos] = useState<{ right: number; bottom: number }>(() => {
    try { const s = localStorage.getItem('catms_chat_pos'); if (s) return JSON.parse(s); } catch { /* ignore */ }
    return DEFAULT_POS;
  });
  const [dragging, setDragging] = useState(false);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const dragRef  = useRef<{ x: number; y: number; right: number; bottom: number } | null>(null);
  const movedRef = useRef(false);
  const savePos = (p: { right: number; bottom: number }) => { try { localStorage.setItem('catms_chat_pos', JSON.stringify(p)); } catch { /* ignore */ } };
  const resetPos = () => { setPos(DEFAULT_POS); savePos(DEFAULT_POS); };

  const onLauncherPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    movedRef.current = false;
    setDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY, right: pos.right, bottom: pos.bottom };
  };
  const onLauncherPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) movedRef.current = true;
    const right  = Math.min(Math.max(8, d.right  - dx), window.innerWidth  - 60);
    const bottom = Math.min(Math.max(8, d.bottom - dy), window.innerHeight - 60);
    setPos({ right, bottom });
  };
  const onLauncherPointerUp = () => {
    setDragging(false);
    if (dragRef.current && movedRef.current) {
      // 左右どちらか近い端に吸着（スナップ）
      const w = launcherRef.current?.offsetWidth ?? 160;
      const h = launcherRef.current?.offsetHeight ?? 40;
      setPos(p => {
        const centerFromRight = p.right + w / 2;
        const snappedRight = centerFromRight > window.innerWidth / 2
          ? window.innerWidth - w - SNAP_MARGIN   // 左端へ
          : SNAP_MARGIN;                            // 右端へ
        const bottom = Math.min(Math.max(SNAP_MARGIN, p.bottom), window.innerHeight - h - SNAP_MARGIN);
        const next = { right: Math.max(SNAP_MARGIN, snappedRight), bottom };
        savePos(next);
        return next;
      });
    }
    dragRef.current = null;
  };

  // ── パネルの展開方向を自動判定（ボタンが上半分なら下向き、下半分なら上向きに開く） ──
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const openUp = pos.bottom < vh / 2;                         // ボタンが画面下半分 → 上に開く
  const panelRight = Math.max(8, Math.min(pos.right, vw - 368));
  const panelVert: React.CSSProperties = openUp
    ? { bottom: pos.bottom + 50 }
    : { top: Math.max(8, vh - pos.bottom + 12) };

  // ── 問い合わせ（サポートチャット）状態 ──────────────────────
  const [tab, setTab]                   = useState<'faq' | 'support'>('faq');
  const [supportView, setSupportView]   = useState<'list' | 'new' | 'thread'>('list');
  const [threads, setThreads]           = useState<SupportThread[]>([]);
  const [activeThread, setActiveThread] = useState<SupportThread | null>(null);
  const [threadMsgs, setThreadMsgs]     = useState<SupportMessage[]>([]);
  const [newSubject, setNewSubject]     = useState('');
  const [newBody, setNewBody]           = useState('');
  const [supportInput, setSupportInput] = useState('');
  const [unread, setUnread]             = useState(0);
  const [sending, setSending]           = useState(false);
  const [peerTyping, setPeerTyping]     = useState(false);
  const supportBottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef     = useRef<HTMLInputElement>(null);
  const lastTypingSent   = useRef(0);

  const fetchUnread = useCallback(async () => {
    if (!api.getToken()) return; // ログアウト済み・セッション切れのタブからは叩かない
    try {
      const res = await api.get<any>('/support/unread-count');
      setUnread(res?.data?.count ?? 0);
    } catch { /* 未ログイン時などは無視 */ }
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.get<any>('/support/threads');
      setThreads(res?.data ?? []);
    } catch { /* ignore */ }
  }, []);

  const fetchThreadMessages = useCallback(async (threadId: string) => {
    try {
      const res = await api.get<any>(`/support/threads/${threadId}/messages`);
      setThreadMsgs(res?.data?.messages ?? []);
      setPeerTyping(!!res?.data?.peer_typing);
      // ステータス（完了/対応中）の変化を反映
      const st = res?.data?.thread?.status;
      if (st) setActiveThread(prev => (prev && prev.id === threadId ? { ...prev, status: st } : prev));
      fetchUnread(); // 開いた分は既読になるので未読数を更新
    } catch { /* ignore */ }
  }, [fetchUnread]);

  // 入力中シグナル（2.5秒に1回に間引いて送信）
  const signalTyping = useCallback((threadId: string) => {
    const now = Date.now();
    if (now - lastTypingSent.current < 2_500) return;
    lastTypingSent.current = now;
    api.post(`/support/threads/${threadId}/typing`, {}).catch(() => {});
  }, []);

  // 添付ファイル送信（画像・PDF、10MBまで）
  const uploadFile = async (file: File) => {
    if (!activeThread || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await fetch(`${api.getApiBase()}/support/threads/${activeThread.id}/attachments`, {
        method: 'POST', headers: authHeaders(), body: fd, credentials: 'include',
      });
      await fetchThreadMessages(activeThread.id);
    } catch { /* ignore */ }
    setSending(false);
  };

  // 未読バッジ: ウィジェットが閉じていても30秒ごとに確認
  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  // 問い合わせ一覧/スレッドのポーリング（開いている間だけ）
  useEffect(() => {
    if (!open || tab !== 'support') return;
    if (supportView === 'list') {
      fetchThreads();
      const t = setInterval(fetchThreads, 15_000);
      return () => clearInterval(t);
    }
    if (supportView === 'thread' && activeThread) {
      fetchThreadMessages(activeThread.id);
      const t = setInterval(() => fetchThreadMessages(activeThread.id), 4_000);
      return () => clearInterval(t);
    }
    return;
  }, [open, tab, supportView, activeThread, fetchThreads, fetchThreadMessages]);

  useEffect(() => {
    supportBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMsgs]);

  const submitNewThread = async () => {
    if (!newSubject.trim() || !newBody.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post<any>('/support/threads', { subject: newSubject.trim(), body: newBody.trim() });
      const id = res?.data?.id;
      setNewSubject(''); setNewBody('');
      await fetchThreads();
      if (id) {
        setActiveThread({ id, subject: newSubject.trim(), status: 'open', unread_count: 0, updated_at: '' });
        setSupportView('thread');
      } else {
        setSupportView('list');
      }
    } catch { /* ignore */ }
    setSending(false);
  };

  const sendSupportMessage = async () => {
    const body = supportInput.trim();
    if (!body || !activeThread || sending) return;
    setSending(true);
    try {
      await api.post<any>(`/support/threads/${activeThread.id}/messages`, { body });
      setSupportInput('');
      await fetchThreadMessages(activeThread.id);
    } catch { /* ignore */ }
    setSending(false);
  };

  const openSupportNew = () => { setTab('support'); setSupportView('new'); };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'bot',
        content: `こんにちは！${BRAND_NAME}に関するご質問にお答えします。\n操作の詳細は「操作マニュアル」でも確認できます。`,
        suggestions: INITIAL_SUGGESTIONS,
        links: [{ label: '操作マニュアルを開く', to: '/manual' }],
      }]);
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = (text: string) => {
    const q = text.trim();
    if (!q) return;

    const { answer, followup, links } = findAnswer(q);

    setMessages(prev => [
      ...prev,
      { role: 'user', content: q },
      { role: 'bot', content: answer, suggestions: followup, links },
    ]);
    setInput('');
  };

  return (
    <>
      {/* フローティングボタン — 閉じているときはラベル付きピル、開いているときは × */}
      {open ? (
        <button
          onClick={() => setOpen(false)}
          title="閉じる"
          style={{
            position: 'fixed', bottom: pos.bottom, right: pos.right, zIndex: 1000,
            width: 40, height: 40, borderRadius: '50%',
            background: 'linear-gradient(135deg, #0066CC, #38BDF8)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0,102,204,0.4)',
          }}
        >
          <X size={18} color="#fff" />
        </button>
      ) : (
        <button
          ref={launcherRef}
          onClick={() => { if (movedRef.current) { movedRef.current = false; return; } setOpen(true); }}
          onPointerDown={onLauncherPointerDown}
          onPointerMove={onLauncherPointerMove}
          onPointerUp={onLauncherPointerUp}
          title="サポート・ヘルプ（ドラッグで移動できます）"
          style={{
            position: 'fixed', bottom: pos.bottom, right: pos.right, zIndex: 1000,
            height: 40, borderRadius: 20,
            background: 'linear-gradient(135deg, #0066CC, #38BDF8)',
            border: 'none', cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none',
            display: 'flex', alignItems: 'center', gap: 8, padding: '0 18px',
            boxShadow: '0 4px 16px rgba(0,102,204,0.35)',
            transition: dragging ? 'none' : 'right 0.2s ease, bottom 0.2s ease, box-shadow 0.15s',
          }}
        >
          <Bot size={16} color="#fff" />
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>ご質問はこちら</span>
          {unread > 0 && (
            <span style={{
              background: '#EF4444', color: '#fff', borderRadius: 10, minWidth: 18, height: 18,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 800, padding: '0 5px',
            }}>{unread}</span>
          )}
        </button>
      )}

      {/* チャットパネル */}
      {open && (
        <div style={{
          position: 'fixed', ...panelVert, right: panelRight, zIndex: 1000,
          width: 360, height: 520, maxWidth: 'calc(100vw - 16px)', maxHeight: 'calc(100vh - 120px)',
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'slideUp 0.2s ease',
        }}>

          {/* ヘッダー */}
          <div style={{
            background: 'linear-gradient(135deg, #0066CC, #38BDF8)',
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Bot size={18} color="#fff" />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{BRAND_NAME}ヘルプ</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>よくある質問にお答えします</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
              <button onClick={resetPos} title="ボタンの位置を右下に戻す" style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6,
                cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 8px', whiteSpace: 'nowrap',
              }}>
                位置リセット
              </button>
              <button onClick={() => setOpen(false)} title="閉じる" style={{
                background: 'none', border: 'none',
                cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: 4,
              }}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* タブ: FAQ / 担当者に問い合わせ */}
          <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
            {([
              { key: 'faq', label: 'よくある質問', icon: <Bot size={13} /> },
              { key: 'support', label: '担当者に問い合わせ', icon: <LifeBuoy size={13} /> },
            ] as const).map(t => (
              <button key={t.key}
                onClick={() => { setTab(t.key); if (t.key === 'support') setSupportView('list'); }}
                style={{
                  flex: 1, padding: '9px 4px', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  fontSize: 12, fontWeight: 700,
                  background: tab === t.key ? '#fff' : 'transparent',
                  color: tab === t.key ? '#0066CC' : '#64748B',
                  borderBottom: tab === t.key ? '2px solid #0066CC' : '2px solid transparent',
                }}>
                {t.icon}{t.label}
                {t.key === 'support' && unread > 0 && (
                  <span style={{ background: '#EF4444', color: '#fff', borderRadius: 8, minWidth: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, padding: '0 4px' }}>{unread}</span>
                )}
              </button>
            ))}
          </div>

          {/* メッセージエリア（FAQ） */}
          {tab === 'faq' && (
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 12px 8px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {messages.map((msg, i) => (
              <div key={i}>
                <div style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '84%',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #0066CC, #38BDF8)'
                      : '#F1F5F9',
                    color: msg.role === 'user' ? '#fff' : '#334155',
                    borderRadius: msg.role === 'user'
                      ? '12px 12px 4px 12px'
                      : '12px 12px 12px 4px',
                    padding: '9px 12px',
                    fontSize: 13, lineHeight: 1.6,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {msg.content}
                  </div>
                </div>

                {/* 画面・マニュアルへのリンク */}
                {msg.role === 'bot' && msg.links && msg.links.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {msg.links.map(link => {
                      const isManual = link.to.startsWith('/manual');
                      return (
                        <button
                          key={link.to + link.label}
                          onClick={() => link.to === 'support:new' ? openSupportNew() : navigate(link.to)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            background: isManual ? '#FEF9EC' : '#EFF6FF',
                            border: `1px solid ${isManual ? '#FDE68A' : '#BFDBFE'}`,
                            borderRadius: 14, padding: '5px 11px',
                            fontSize: 12, fontWeight: 700,
                            color: isManual ? '#B45309' : '#0066CC',
                            cursor: 'pointer',
                          }}
                        >
                          {isManual ? <BookOpen size={12} /> : <ExternalLink size={12} />}
                          {link.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* サジェスト */}
                {msg.role === 'bot' && msg.suggestions && msg.suggestions.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {msg.suggestions.map(s => (
                      <button
                        key={s}
                        onClick={() => sendMessage(s)}
                        style={{
                          textAlign: 'left', background: '#F8FAFC',
                          border: '1px solid #E2E8F0', borderRadius: 8,
                          padding: '7px 10px', fontSize: 12, color: '#0066CC',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                        onMouseLeave={e => e.currentTarget.style.background = '#F8FAFC'}
                      >
                        <ChevronRight size={12} style={{ flexShrink: 0 }} />
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          )}

          {/* 入力エリア（FAQ） */}
          {tab === 'faq' && (
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid #F1F5F9',
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(input); } }}
              placeholder="質問を入力..."
              style={{
                flex: 1, padding: '8px 12px',
                border: '1.5px solid #E2E8F0', borderRadius: 20,
                fontSize: 13, outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor = '#0066CC'}
              onBlur={e => e.target.style.borderColor = '#E2E8F0'}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim()}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: input.trim() ? 'linear-gradient(135deg, #0066CC, #38BDF8)' : '#E2E8F0',
                border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              <Send size={15} color={input.trim() ? '#fff' : '#94A3B8'} />
            </button>
          </div>
          )}

          {/* 問い合わせ: 一覧 */}
          {tab === 'support' && supportView === 'list' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button onClick={() => setSupportView('new')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px', background: 'linear-gradient(135deg, #0066CC, #38BDF8)',
                  border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                <Plus size={14} /> 新規問い合わせ
              </button>
              {threads.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 12.5, padding: '24px 8px', lineHeight: 1.8 }}>
                  対応中の問い合わせはありません。<br />システムについてのご質問・不具合報告など、<br />お気軽にお問い合わせください。
                </div>
              )}
              <div style={{ textAlign: 'center', fontSize: 10.5, color: '#CBD5E1', paddingTop: 2 }}>
                ※ 対応完了した問い合わせはここには表示されません
              </div>
              {threads.map(t => (
                <button key={t.id}
                  onClick={() => { setActiveThread(t); setSupportView('thread'); setThreadMsgs([]); }}
                  style={{
                    textAlign: 'left', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10,
                    padding: '10px 12px', cursor: 'pointer',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 800, borderRadius: 6, padding: '1px 7px',
                      background: t.status === 'open' ? '#EFF6FF' : '#F1F5F9',
                      color: t.status === 'open' ? '#0066CC' : '#94A3B8',
                    }}>{t.status === 'open' ? '対応中' : '完了'}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                    {t.unread_count > 0 && (
                      <span style={{ background: '#EF4444', color: '#fff', borderRadius: 8, minWidth: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, padding: '0 4px' }}>{t.unread_count}</span>
                    )}
                  </div>
                  {t.last_message && (
                    <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.last_message}</div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* 問い合わせ: 新規作成 */}
          {tab === 'support' && supportView === 'new' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => setSupportView('list')}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 12, fontWeight: 600, padding: 0 }}>
                <ArrowLeft size={13} /> 一覧に戻る
              </button>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>システム担当者に問い合わせ</div>
              <div style={{ fontSize: 11.5, color: '#94A3B8', lineHeight: 1.7 }}>
                送信すると担当者に通知されます。返信はこのチャットに届きます。
              </div>
              <input
                value={newSubject}
                onChange={e => setNewSubject(e.target.value)}
                placeholder="件名（例: 打刻データの修正について）"
                style={{ padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, outline: 'none' }}
                onFocus={e => e.target.style.borderColor = '#0066CC'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
              <textarea
                value={newBody}
                onChange={e => setNewBody(e.target.value)}
                placeholder="お問い合わせ内容を入力してください"
                rows={7}
                style={{ padding: '9px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                onFocus={e => e.target.style.borderColor = '#0066CC'}
                onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              />
              <button
                onClick={submitNewThread}
                disabled={!newSubject.trim() || !newBody.trim() || sending}
                style={{
                  padding: '11px', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
                  background: newSubject.trim() && newBody.trim() && !sending ? 'linear-gradient(135deg, #0066CC, #38BDF8)' : '#E2E8F0',
                  color: newSubject.trim() && newBody.trim() && !sending ? '#fff' : '#94A3B8',
                  cursor: newSubject.trim() && newBody.trim() && !sending ? 'pointer' : 'not-allowed',
                }}>
                {sending ? '送信中...' : '送信する'}
              </button>
            </div>
          )}

          {/* 問い合わせ: スレッド（チャット） */}
          {tab === 'support' && supportView === 'thread' && activeThread && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid #F1F5F9', background: '#FBFDFF' }}>
                <button onClick={() => { setSupportView('list'); fetchThreads(); }}
                  style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: 2 }}>
                  <ArrowLeft size={14} />
                </button>
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activeThread.subject}</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {threadMsgs.map(m => m.sender_type === 'system' ? (
                  <div key={m.id} style={{ textAlign: 'center', fontSize: 11, color: '#94A3B8', padding: '2px 8px' }}>
                    ── {m.body} ──
                  </div>
                ) : (
                  <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_type === 'admin' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '84%' }}>
                      {m.sender_type === 'super' && (
                        <div style={{ fontSize: 10.5, color: '#94A3B8', marginBottom: 2, fontWeight: 600 }}>🛟 {m.sender_name}（サポート）</div>
                      )}
                      <div style={{
                        background: m.sender_type === 'admin' ? 'linear-gradient(135deg, #0066CC, #38BDF8)' : '#F1F5F9',
                        color: m.sender_type === 'admin' ? '#fff' : '#334155',
                        borderRadius: m.sender_type === 'admin' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        padding: '8px 12px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {m.body}
                        {m.attachment_path && (
                          <AttachmentView messageId={m.id} name={m.attachment_name ?? 'ファイル'} mime={m.attachment_mime ?? ''} />
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 2, textAlign: m.sender_type === 'admin' ? 'right' : 'left' }}>
                        {m.created_at?.replace('T', ' ').slice(5, 16)}
                      </div>
                    </div>
                  </div>
                ))}
                {threadMsgs.length === 0 && <div style={{ textAlign: 'center', color: '#CBD5E1', fontSize: 12, padding: 16 }}>読み込み中...</div>}
                {peerTyping && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#64748B', padding: '2px 4px' }}>
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <span className="typing-dot" /><span className="typing-dot" style={{ animationDelay: '0.15s' }} /><span className="typing-dot" style={{ animationDelay: '0.3s' }} />
                    </span>
                    サポート担当が入力中...
                  </div>
                )}
                <div ref={supportBottomRef} />
              </div>
              {activeThread.status === 'closed' && (
                <div style={{ padding: '10px 12px', background: '#F8FAFC', borderTop: '1px solid #F1F5F9', textAlign: 'center' }}>
                  <div style={{ fontSize: 11.5, color: '#64748B', marginBottom: 8 }}>
                    ✅ この問い合わせは対応完了しました。ご利用ありがとうございました。
                  </div>
                  <button onClick={() => { setSupportView('new'); fetchThreads(); }}
                    style={{ padding: '7px 16px', background: 'linear-gradient(135deg, #0066CC, #38BDF8)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    続きのご用件は新規問い合わせへ
                  </button>
                </div>
              )}
              {activeThread.status !== 'closed' && (
              <div style={{ padding: '10px 12px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadFile(f); }} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  title="画像・PDFを添付（10MBまで）"
                  style={{
                    width: 34, height: 34, borderRadius: '50%', background: '#F1F5F9',
                    border: 'none', cursor: sending ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                  <Paperclip size={15} color="#64748B" />
                </button>
                <input
                  value={supportInput}
                  onChange={e => { setSupportInput(e.target.value); if (e.target.value && activeThread) signalTyping(activeThread.id); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendSupportMessage(); } }}
                  placeholder="メッセージを入力..."
                  style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 20, fontSize: 13, outline: 'none', minWidth: 0 }}
                  onFocus={e => e.target.style.borderColor = '#0066CC'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
                <button
                  onClick={sendSupportMessage}
                  disabled={!supportInput.trim() || sending}
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: supportInput.trim() && !sending ? 'linear-gradient(135deg, #0066CC, #38BDF8)' : '#E2E8F0',
                    border: 'none', cursor: supportInput.trim() && !sending ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                  <Send size={15} color={supportInput.trim() && !sending ? '#fff' : '#94A3B8'} />
                </button>
              </div>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .typing-dot {
          width: 5px; height: 5px; border-radius: 50%; background: #94A3B8;
          display: inline-block; animation: typingBounce 1s infinite;
        }
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-3px); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
